import { FC, useEffect, useState } from 'react'

// A video hosted on Google Drive or Vimeo, by its file/video id.
// `fallback` is shown instead when a Vimeo video can't be embedded on this site (privacy settings).
export type VideoRef = { host: 'drive' | 'vimeo'; id: string; fallback?: VideoRef }

// autoplay: Vimeo starts playing straight away, muted (browsers only allow autoplay without sound).
// Drive's player can't autoplay, so the fallback always waits for a click.
const embedUrl = ({ host, id }: VideoRef, autoplay = false) =>
  host === 'vimeo'
    ? `https://player.vimeo.com/video/${id}${autoplay ? '?autoplay=1&muted=1' : ''}`
    : `https://drive.google.com/file/d/${id}/preview`

// Vimeo's oEmbed reply has `domain_status_code: 403` when the video's privacy settings don't allow
// embedding on the requesting page's domain (the browser sends it as the referrer), and is a 404
// for private or deleted videos. An embeddable video has no domain_status_code, or 200.
async function canEmbedVimeo(id: string, signal: AbortSignal) {
  const url = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}`
  const res = await fetch(url, { signal })
  if (!res.ok) return false
  const data = await res.json()
  return data.domain_status_code === undefined || data.domain_status_code === 200
}

// Picks the video to show: a Vimeo video with a fallback is checked first, and is null while checking.
function usePlayableVideo(video: VideoRef): VideoRef | null {
  const fallback = video.host === 'vimeo' ? video.fallback : undefined
  // result of the Vimeo check, for the video id it was made for
  const [checked, setChecked] = useState<{ id: string; ok: boolean } | null>(null)

  useEffect(() => {
    if (!fallback) return
    const controller = new AbortController()
    canEmbedVimeo(video.id, controller.signal)
      .then((ok) => setChecked({ id: video.id, ok }))
      .catch(() => {
        if (!controller.signal.aborted) setChecked({ id: video.id, ok: false })
      })
    return () => controller.abort()
  }, [video.id, fallback])

  if (!fallback) return video
  if (checked?.id !== video.id) return null
  return checked.ok ? video : fallback
}

const EmbeddedVideo: FC<{
  video: VideoRef
  title?: string
  className?: string
  // start playing on load, muted (Vimeo only)
  autoplay?: boolean
}> = ({
  video,
  title,
  autoplay = false,
  // width classes; defaults to a centred, capped width
  className = 'max-w-2xl mx-auto',
}) => {
  const playable = usePlayableVideo(video)

  return (
    // Drive: taller on mobile, its player adds a title bar above the video there, which would cut off the bottom
    <div
      className={`${playable?.host === 'drive' ? 'aspect-square md:aspect-video' : 'aspect-video'} w-full ${className}`}
    >
      {playable ? (
        <iframe
          src={embedUrl(playable, autoplay)}
          title={title}
          className='w-full h-full rounded-lg'
          allow='autoplay; fullscreen; picture-in-picture'
          allowFullScreen
        />
      ) : (
        // placeholder of the same size while checking Vimeo, so the page doesn't jump
        <div className='w-full h-full rounded-lg bg-gray-200 animate-pulse' />
      )}
    </div>
  )
}

export default EmbeddedVideo
